/* global document, window, IntersectionObserver */
import { __ } from '@wordpress/i18n';

(function () {
  const blocks = document.querySelectorAll('.wp-block-custom-cta-buttons');
  if (!blocks.length) return;

  /* ===== Viewport estável no mobile: define --vh (freeze/unfreeze com modal) ===== */
  const VhCtrl = (() => {
    let rafId = null;
    let frozen = false;
    let last = 0;

    const measure = () =>
      (window.visualViewport ? window.visualViewport.height : window.innerHeight);

    const setVH = (force = false) => {
      if (frozen && !force) return;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const h = measure();
        if (!force && Math.abs(h - last) < 4) return;
        last = h;
        document.documentElement.style.setProperty('--vh', h * 0.01 + 'px');
      });
    };

    const freeze = () => { frozen = true; };
    const unfreeze = () => { frozen = false; setVH(true); };

    setVH(true);
    const passive = { passive: true };
    window.addEventListener('resize', setVH, passive);
    window.addEventListener('orientationchange', () => setVH(true), passive);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setVH, passive);
      window.visualViewport.addEventListener('scroll', setVH, passive);
    }

    return { setVH, freeze, unfreeze };
  })();

  /* ===== Body lock (impede scroll do fundo em iOS/Android) ===== */
  const BodyLock = (() => {
    let locked = false;
    let scrollY = 0;

    function lock() {
      if (locked) return;
      locked = true;
      scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.classList.add('cta-modal-open');
    }
    function unlock() {
      if (!locked) return;
      locked = false;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.classList.remove('cta-modal-open');
      window.scrollTo(0, scrollY);
    }
    return { lock, unlock, get locked() { return locked; } };
  })();

  const BOOK_API =
    window.ctaBookApi ||
    (window.wpApiSettings && window.wpApiSettings.root
      ? window.wpApiSettings.root.replace(/\/+$/, '') + '/cta/v1/book'
      : window.location.origin + '/index.php?rest_route=/cta/v1/book');

  const NEWS_API = BOOK_API.replace('/book', '/newsletter');

  const cacheBust = (id) =>
    `&_=${Date.now()}-${Math.random().toString(36).slice(2)}-${id}`;

  function getRootFromModal(modal) {
    const idx = modal?.dataset?.ctaIndex;
    if (idx != null) {
      const r = document.querySelector(`.wp-block-custom-cta-buttons[data-cta-index="${idx}"]`);
      if (r) return r;
    }
    return modal.closest('.wp-block-custom-cta-buttons');
  }

  const LoadedSDK = new Map();
  function ensureMauticScript(domain) {
    const src = `${domain}/media/js/mautic-form.js`;
    if (window.MauticSDKLoaded) return Promise.resolve();
    if (LoadedSDK.has(src)) return LoadedSDK.get(src);
    const p = new Promise((resolve) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) existing.addEventListener('load', resolve, { once: true });
      else {
        const s = document.createElement('script');
        s.src = src; s.async = true; s.onload = resolve;
        document.head.appendChild(s);
      }
    });
    LoadedSDK.set(src, p);
    return p;
  }

  function showLoading(slot) {
    slot.innerHTML = '<div class="cta-loading"><div class="cta-spinner" aria-hidden="true"></div></div>';
  }

  function findWrapperByFormId(formId) {
    const all = document.querySelectorAll('.mauticform_wrapper');
    for (const wrap of all) {
      const hidden = wrap.querySelector('input[name="mauticform[formId]"]');
      if (hidden && String(hidden.value) === String(formId)) return wrap;
    }
    return null;
  }

  function waitForWrapper(formId, { timeout = 8000 } = {}) {
    return new Promise((resolve) => {
      const existing = findWrapperByFormId(formId);
      if (existing) { resolve(existing); return; }
      let done = false;
      const finish = (node) => { if (!done) { done = true; obs.disconnect(); clearTimeout(t); resolve(node || null); } };
      const obs = new MutationObserver(() => {
        const w = findWrapperByFormId(formId);
        if (w) finish(w);
      });
      obs.observe(document.documentElement, { childList: true, subtree: true });
      const t = setTimeout(() => finish(null), timeout);
    });
  }

  async function injectMauticInto(slot, { domain, formId }) {
    if (!slot) return;
    const MAUTIC_DOMAIN = (domain || '').replace(/\/+$/, '');
    const MAUTIC_FORMID = formId || '';
    if (!MAUTIC_DOMAIN || !MAUTIC_FORMID) {
      slot.innerHTML = '<p style="opacity:.85;line-height:1.5;margin:8px 0">' +
        __('Configure o domínio e o ID do formulário no bloco.', 'cta-block') + '</p>';
      return;
    }

    const already = slot.querySelector('.mauticform_wrapper');
    if (already) {
      const hid = already.querySelector('input[name="mauticform[formId]"]');
      if (hid && String(hid.value) === String(MAUTIC_FORMID)) return;
    }

    if (slot.dataset.loading === '1') return;
    slot.dataset.loading = '1';
    showLoading(slot);

    try {
      await ensureMauticScript(MAUTIC_DOMAIN);
      const sc = document.createElement('script');
      sc.src = `${MAUTIC_DOMAIN}/form/generate.js?id=${encodeURIComponent(MAUTIC_FORMID)}${cacheBust(MAUTIC_FORMID)}`;
      document.body.appendChild(sc);

      const wrapper = await waitForWrapper(MAUTIC_FORMID, { timeout: 10000 });
      const loading = slot.querySelector('.cta-loading');
      if (loading) loading.remove();
      slot.dataset.loading = '0';

      if (wrapper) {
        slot.innerHTML = '';
        slot.appendChild(wrapper);
        slot.querySelectorAll('.mauticform_wrapper style, style#mauticform-style').forEach((el) => el.remove());
        document.querySelectorAll('link[href*="mautic-form.css"]').forEach((l) => (l.disabled = true));
      } else {
        slot.innerHTML =
          `<p style="opacity:.85">${__('Não foi possível carregar o formulário agora.', 'cta-block')}</p>` +
          `<p><a href="${MAUTIC_DOMAIN}/form/${encodeURIComponent(MAUTIC_FORMID)}" target="_blank" rel="noopener">${__('Abrir formulário numa nova janela', 'cta-block')}</a></p>`;
      }
    } catch (err) {
      slot.dataset.loading = '0';
      slot.innerHTML =
        `<p style="opacity:.85">${__('Ocorreu um erro a carregar o formulário.', 'cta-block')}</p>` +
        `<p><a href="${MAUTIC_DOMAIN}/form/${encodeURIComponent(MAUTIC_FORMID)}" target="_blank" rel="noopener">${__('Abrir formulário numa nova janela', 'cta-block')}</a></p>`;
    }
  }

  // Calendly
  let CalendlyAssetsPromise;
  function ensureCalendlyAssets() {
    if (window.Calendly) return Promise.resolve();
    if (CalendlyAssetsPromise) return CalendlyAssetsPromise;
    CalendlyAssetsPromise = new Promise((resolve) => {
      if (!document.querySelector('link[href*="assets.calendly.com/assets/external/widget.css"]')) {
        const l = document.createElement('link'); l.rel = 'stylesheet';
        l.href = 'https://assets.calendly.com/assets/external/widget.css';
        document.head.appendChild(l);
      }
      const existing = document.querySelector('script[src*="assets.calendly.com/assets/external/widget.js"]');
      if (existing) existing.addEventListener('load', resolve, { once: true });
      else {
        const s = document.createElement('script');
        s.src = 'https://assets.calendly.com/assets/external/widget.js'; s.async = true; s.onload = resolve;
        document.head.appendChild(s);
      }
    });
    return CalendlyAssetsPromise;
  }

  async function injectCalendlyInto(slot) {
    if (!slot) return;
    const modal = slot.closest('.cta-modal');
    const root  = modal ? getRootFromModal(modal) : slot.closest('.wp-block-custom-cta-buttons');
    const cfgEl = root?.querySelector('.cta-config');
    const urlFromData = (slot.dataset?.calendlyUrl || '').trim();
    const url = urlFromData || (cfgEl?.dataset?.calendlyUrl || '').trim();
    if (!url) { slot.innerHTML = `<p style="opacity:.85">${__('Defina o Calendly URL nas opções do bloco.', 'cta-block')}</p>`; return; }
    if (slot.querySelector('.calendly-inline-widget')) return;

    showLoading(slot);
    await ensureCalendlyAssets();
    slot.innerHTML = '';
    try { while (slot.firstChild) slot.removeChild(slot.firstChild); } catch (_) {}
    window.Calendly.initInlineWidget({ url, parentElement: slot });
  }

  // Flow da Mentoria
  function startMentoriaFlow(modal) {
    if (modal.dataset.bound === '1') return;
    modal.dataset.bound = '1';

    const root  = getRootFromModal(modal);
    const cfgEl = root?.querySelector('.cta-config');
    const formEl = modal.querySelector('form[data-mentoria]');
    const slot   = modal.querySelector('.calendly-slot');
    if (!formEl || !slot) return;

    if (!slot.id) slot.id = `calendly-slot-${root.dataset.ctaIndex || '0'}`;

    const calendlyUrl      = (slot.dataset.calendlyUrl || cfgEl?.dataset?.calendlyUrl || '').trim();
    const mauticDomain     = (cfgEl?.dataset?.mauticDomain || '').trim();
    const mentoriaFormId   = (cfgEl?.dataset?.mentoriaFormId || '').trim();
    const mentoriaTagsStr  = (cfgEl?.dataset?.mentoriaTags || '').trim();
    const newsletterFormId = (cfgEl?.dataset?.newsletterFormId || '').trim();

    const lastnameFieldHtml = (cfgEl?.dataset?.lastnameField || 'ultimo_nome').trim();
    const consentFieldHtml  = (cfgEl?.dataset?.consentField  || 'marketing_optin').trim();

    formEl.addEventListener('submit', async (e) => {
      e.preventDefault();
      const fd = new FormData(formEl);
      const nome   = (fd.get('nome') || '').toString().trim();
      const email  = (fd.get('email') || '').toString().trim();
      const lnForm = (fd.get('ultimo_nome') || fd.get('apelido') || '').toString().trim();
      const wantsNewsletter = !!fd.get('newsletter');
      if (!nome || !email) { alert(__('Preencha nome e email.', 'cta-block')); return; }

      const parts = nome.split(/\s+/).filter(Boolean);
      const firstname = parts.length > 1 ? parts.slice(0, -1).join(' ') : (parts[0] || '');
      const lastname  = lnForm || (parts.length > 1 ? parts[parts.length - 1] : '');

      const buildFields = (withConsent) => {
        const obj = {
          firstname,
          first_name: firstname,
          name: nome,
          lastname,
          last_name: lastname,
          nome: firstname,
          apelido: lastname,
          sobrenome: lastname,
          [lastnameFieldHtml]: lastname,
        };
        if (withConsent) {
          obj[consentFieldHtml] = '1';
          obj.marketing_optin = '1';
        }
        return obj;
      };

      if (mentoriaFormId && mauticDomain) {
        try {
          await fetch(NEWS_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              mauticDomain,
              formId: mentoriaFormId,
              email,
              name: nome,
              fields: buildFields(wantsNewsletter),
              tags: mentoriaTagsStr ? mentoriaTagsStr.split(',').map(s => s.trim()).filter(Boolean) : [],
            }),
          });
        } catch (_) {}
      }

      if (wantsNewsletter && newsletterFormId && mauticDomain) {
        try {
          await fetch(NEWS_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              mauticDomain,
              formId: newsletterFormId,
              email,
              name: nome,
              fields: buildFields(true),
            }),
          });
        } catch (_) {}
      }

      if (!calendlyUrl) { alert(__('Calendly URL não está configurado.', 'cta-block')); return; }

      const successP = formEl.querySelector('.mentoria-success');
      if (successP) successP.hidden = false;
      formEl.style.display = 'none';
      slot.style.display = '';

      await ensureCalendlyAssets();
      try { while (slot.firstChild) slot.removeChild(slot.firstChild); } catch (_) {}
      window.Calendly.initInlineWidget({
        url: calendlyUrl,
        parentElement: slot,
        prefill: { name: nome, email },
        utm: { source: 'site', medium: 'cta-modal', campaign: 'mentoria' },
      });
    });

    window.addEventListener('message', (event) => {
      if (!/calendly\.com/.test(event.origin)) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data && data.event && data.event.indexOf('calendly.event_scheduled') !== -1) {
          /* opcional */
        }
      } catch (_) {}
    }, { passive: true });
  }

  /* ===========================
     Mautic Focus
     =========================== */
  const DEFAULT_FOCUS_CSS = `
/* ====== ESCOPO DO FOCUS ====== */
.mautic-focus:not(.mf-noscoped) {
  --cta-font: var(--wp--preset--font-family--mp-grotesk, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial) !important;
  --cta-label-color: #a5b2c7 !important;
  --cta-label-size: 14px !important;
  --cta-input-bg: #0f1a2c !important;
  --cta-input-fg: #fff !important;
  --cta-input-border: #2e3b54 !important;
  --cta-input-radius: 10px !important;
  --cta-input-pad: 10px 12px !important;
  --cta-focus-outline: #29406a !important;
  --cta-focus-border: #3a5a8f !important;
}
.mautic-focus:not(.mf-noscoped),
.mautic-focus:not(.mf-noscoped) *:where(:not(i,svg,code,kbd,pre)) {
  font-family: var(--cta-font) !important;
}
.mautic-focus .mautic-focus-overlay { background: rgba(0,0,0,.55) !important; }
.mautic-focus .mautic-focus-modal {
  background: #0b172a !important; color: #fff !important;
  border-radius: 16px !important; padding: 40px 28px !important;
  box-shadow: 0 10px 32px rgba(0,0,0,.4) !important; border: 0 !important;
}
.mautic-focus .mautic-focus-close { top: 8px !important; right: 10px !important; color: #a5b2c7 !important; opacity:1 !important; font-size: 24px !important; }
.mautic-focus .mautic-focus-main, .mautic-focus .mautic-focus-content, .mautic-focus .mautic-focus-innerwrap { background: transparent !important; border:0 !important; box-shadow:none !important; }
.mautic-focus .mauticform-page-wrapper { display:grid !important; grid-template-columns:1fr !important; gap:16px !important; }
@media (min-width:640px){ .mautic-focus .mauticform-page-wrapper{ grid-template-columns:1fr 1fr !important; } .mautic-focus .mauticform-textarea, .mautic-focus .mauticform-button-wrapper{ grid-column: 1 / -1 !important; } }
.mautic-focus .mauticform-label, .mautic-focus .mauticform-radiogrp-label, .mautic-focus .mauticform-checkboxgrp-label { color:var(--cta-label-color) !important; font-size:var(--cta-label-size) !important; margin-bottom:6px !important; }
.mautic-focus .mauticform-row input, .mautic-focus .mauticform-row textarea, .mautic-focus .mauticform-row select, .mautic-focus .mauticform-input, .mautic-focus .mauticform-textarea, .mautic-focus .mauticform-selectbox {
  background:var(--cta-input-bg) !important; color:var(--cta-input-fg) !important; border:1px solid var(--cta-input-border) !important; border-radius:var(--cta-input-radius) !important; padding:var(--cta-input-pad) !important; width:100% !important; max-width:100% !important; box-sizing:border-box !important;
}
.mautic-focus .mauticform-row input:focus, .mautic-focus .mauticform-row textarea:focus, .mautic-focus .mauticform-row select:focus { outline:2px solid var(--cta-focus-outline) !important; outline-offset:1px !important; border-color:var(--cta-focus-border) !important; }
.mautic-focus ::placeholder { color: rgba(255,255,255,.65) !important; }
.mautic-focus .mauticform-radiogrp, .mautic-focus .mauticform-checkboxgrp { display:grid !important; row-gap:12px !important; }
.mautic-focus .mauticform-radiogrp-row, .mautic-focus .mauticform-checkboxgrp-row { --control-size:1.25rem; display:grid !important; grid-template-columns:var(--control-size) 1fr !important; column-gap:10px !important; align-items:start !important; }
.mautic-focus .mauticform-button, .mautic-focus .mauticform-button[type="submit"] {
  background:#fff !important; color:#0b172a !important; border:0 !important; border-radius:4px !important; padding:1rem 5.06rem !important;
  font-weight:300 !important; text-transform:uppercase !important; font-size:var(--wp--preset--font-size--label,14px) !important;
  box-shadow:0 4px 16px rgba(0,0,0,.18), inset 0 -2px rgba(0,0,0,.08) !important; transition:transform .08s ease, box-shadow .2s ease !important;
}
.mautic-focus .mauticform-button:hover { transform:translateY(-1px) !important; }

/* === Alvo correto: iframe do Focus na página MÃE === */
.mf-modal-iframe {
  width: 94vw !important;
  max-width: 1140px !important;
  height: auto !important;
  max-height: 90svh !important;
  border: 0 !important;
  background: transparent !important;
}
.mf-modal-iframe:not(.mf-loaded){ pointer-events:none !important; }
`.trim();

  function injectFocusCSS(css) {
    if (!css) return;
    if (document.querySelector('style[data-cta-focus-default="1"]')) return;
    const st = document.createElement('style');
    st.type = 'text/css';
    st.dataset.ctaFocusDefault = '1';
    st.appendChild(document.createTextNode(css));
    document.head.appendChild(st);
  }

  function loadMauticFocus(domain, id) {
    const src = `${domain.replace(/\/+$/, '')}/focus/${encodeURIComponent(id)}.js`;
    if (document.querySelector(`script[src="${src}"]`)) return;
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.charset = 'utf-8';
    document.body.appendChild(s);
  }

  /* ===== Observer de sizing do Focus (agora apanha .mf-modal-iframe) ===== */
  (function setupFocusSizing() {
    const MAX_W_PX = 1140;
    const SAFE_MIN_H = 600;
    const VH_CAP = 0.90;

    function vwWidth(pxCap){ return Math.round(Math.min(window.innerWidth * 0.94, pxCap)) + 'px'; }
    function vhCap(){ return Math.round(window.innerHeight * VH_CAP) + 'px'; }

    function sizeFrame(iframe){
      if (!iframe) return;
      iframe.style.setProperty('width', vwWidth(MAX_W_PX), 'important');
      iframe.style.setProperty('max-width', MAX_W_PX + 'px', 'important');
      iframe.style.setProperty('max-height', vhCap(), 'important');
      let h = SAFE_MIN_H;
      try {
        const doc = iframe.contentDocument;
        if (doc && doc.documentElement) {
          const sH = Math.max(
            doc.documentElement.scrollHeight,
            doc.body ? doc.body.scrollHeight : 0,
            doc.documentElement.offsetHeight,
            doc.body ? doc.body.offsetHeight : 0
          );
          if (sH) h = Math.min(sH, Math.round(window.innerHeight * VH_CAP));
        }
      } catch(_) {}
      iframe.style.setProperty('height', h + 'px', 'important');
    }

    function bind(iframe){
      if (!iframe || iframe.dataset.focusBound) return;
      iframe.dataset.focusBound = '1';
      sizeFrame(iframe);
      const mo = new MutationObserver(() => sizeFrame(iframe));
      mo.observe(iframe, { attributes: true, attributeFilter: ['class','style'] });
      window.addEventListener('resize', () => sizeFrame(iframe));
    }

    function scan(){
      document.querySelectorAll('.mf-modal-iframe').forEach(bind);
    }

    scan();
    const domObserver = new MutationObserver(scan);
    domObserver.observe(document.documentElement, { childList: true, subtree: true });
  })();

  /* ===== Hardening: força refit em toggles do DevTools/resize ===== */
  (function focusRefit() {
    const MAX_W = 1140;
    const VH_CAP = 0.90;

    function apply(iframe) {
      if (!iframe) return;
      try {
        iframe.style.removeProperty('width');
        iframe.style.removeProperty('height');
        iframe.style.removeProperty('max-width');
        iframe.style.removeProperty('max-height');

        iframe.style.setProperty('width', `min(94vw, ${MAX_W}px)`, 'important');
        iframe.style.setProperty('max-width', `${MAX_W}px`, 'important');

        const h = Math.round(window.innerHeight * VH_CAP) + 'px';
        iframe.style.setProperty('height', h, 'important');
        iframe.style.setProperty('max-height', h, 'important');
      } catch (_) {}
    }

    function refitAll(){
      document.querySelectorAll('.mf-modal-iframe').forEach(apply);
    }

    ['resize', 'orientationchange', 'visibilitychange'].forEach(evt => {
      window.addEventListener(evt, refitAll, { passive: true });
    });

    const mo = new MutationObserver(() => {
      const iframes = document.querySelectorAll('.mf-modal-iframe');
      if (!iframes.length) return;
      let ticks = 0;
      function tick() {
        refitAll();
        if (++ticks < 15) requestAnimationFrame(tick);
      }
      tick();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });

    refitAll();
  })();

  // Registry & triggers
  const Registry = [];
  blocks.forEach((root, regIndex) => {
    const qs  = (sel, ctx = root) => ctx.querySelector(sel);
    const qsa = (sel, ctx = root) => Array.from(ctx.querySelectorAll(sel));
    root.dataset.ctaIndex = String(regIndex);

    const cfg = qs('.cta-config');
    const MAUTIC_DOMAIN = (cfg?.dataset?.mauticDomain || '').replace(/\/+$/, '');
    const MAUTIC_FORMID  = cfg?.dataset?.mauticFormId || '';

    const FOCUS_ID    = (cfg?.dataset?.focusId || '').trim();
    const FOCUS_DELAY = Math.max(0, parseInt(cfg?.dataset?.focusDelay || '8', 10));

    if (FOCUS_ID && !document.querySelector('.cta-focus-css')) {
      injectFocusCSS(DEFAULT_FOCUS_CSS);
    }
    if (FOCUS_ID && MAUTIC_DOMAIN) {
      const schedule = () => loadMauticFocus(MAUTIC_DOMAIN, FOCUS_ID);
      const afterDelay = () => setTimeout(schedule, FOCUS_DELAY * 1000);
      if (document.readyState === 'complete' || document.readyState === 'interactive') afterDelay();
      else window.addEventListener('DOMContentLoaded', afterDelay, { once: true });
    }

    const modals = {
      mautic: qs('.cta-modal[data-modal="mautic"]'),
      mentoria: qs('.cta-modal[data-modal="mentoria"]'),
    };

    function portalize(modal, rootEl) {
      if (!modal || modal.dataset.ctaPortalized) return;
      modal.dataset.ctaIndex = rootEl.dataset.ctaIndex || '0';
      document.body.appendChild(modal);
      modal.dataset.ctaPortalized = '1';
    }

    function openModal(type) {
      const modal = modals[type];
      if (!modal) return;
      portalize(modal, root);
      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('is-open');

      BodyLock.lock();
      VhCtrl.freeze();

      const dialog = modal.querySelector('.cta-modal__dialog');
      if (dialog) {
        dialog.setAttribute('tabindex', '-1');
        dialog.focus();
      }
    }

    function closeModal(modal) {
      if (!modal) return;
      modal.setAttribute('aria-hidden', 'true');
      modal.classList.remove('is-open');

      if (!document.querySelector('.cta-modal.is-open')) {
        BodyLock.unlock();
        VhCtrl.unfreeze();
      }
    }

    ['mautic', 'mentoria'].forEach((t) => {
      const m = modals[t];
      if (!m) return;
      m.querySelector('.cta-modal__backdrop')?.addEventListener('click', () => closeModal(m));
      m.querySelector('.cta-modal__close')?.addEventListener('click', () => closeModal(m));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') document.querySelectorAll('.cta-modal.is-open').forEach(closeModal);
    });

    if (MAUTIC_DOMAIN) {
      const warm = () => ensureMauticScript(MAUTIC_DOMAIN);
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => { if (en.isIntersecting) { warm(); io.disconnect(); } });
        }, { rootMargin: '200px' });
        io.observe(root);
      } else {
        (window.requestIdleCallback || window.setTimeout)(warm, 800);
      }
    }

const firstTwoLinks = qsa('.wp-block-buttons .wp-block-button .wp-block-button__link').slice(0, 2);
const mauticBtn = firstTwoLinks[0];
const mentoriaBtn = firstTwoLinks[1];



    function openMauticFromThisBlock() {
      openModal('mautic');
      const slot = modals.mautic?.querySelector('.mautic-slot');
      if (slot) injectMauticInto(slot, { domain: MAUTIC_DOMAIN, formId: MAUTIC_FORMID });
    }
    function openMentoriaFromThisBlock() {
      openModal('mentoria');
      startMentoriaFlow(modals.mentoria);
    }

    if (mauticBtn) {
      mauticBtn.addEventListener('click', (e) => { e.preventDefault(); openMauticFromThisBlock(); });
    }
    if (mentoriaBtn) {
      mentoriaBtn.addEventListener('click', (e) => { e.preventDefault(); openMentoriaFromThisBlock(); });
    }

    Registry.push({ root, openMautic: openMauticFromThisBlock, openMentoria: openMentoriaFromThisBlock });
  });

  (function bindExternalTriggers() {
    if (!Registry.length) return;
    const anchors = document.querySelectorAll('a[href^="#mautic"], a[href^="#mentoria"]');
    anchors.forEach((a) => {
      if (a.dataset.ctaBound) return;
      a.dataset.ctaBound = '1';
      a.addEventListener('click', (e) => {
        const m = /^#(mautic|mentoria)(?:@(\d+))?$/i.exec(a.getAttribute('href') || '');
        if (!m) return;
        e.preventDefault();
        const type = m[1].toLowerCase();
        const index = Math.max(0, Math.min(Registry.length - 1, parseInt(m[2] || '0', 10)));
        const entry = Registry[index];
        if (!entry) return;
        if (type === 'mautic') entry.openMautic(); else entry.openMentoria();
      });
    });
  })();
})();
